// @ts-check
import { readRace, readBest } from './race.js';
import { MEDALS, targetTime, timeLimit, availableBits } from './stage-goals.js';
export { MEDALS, targetTime, timeLimit, availableBits } from './stage-goals.js';
/** @typedef {import('./types.js').LevelDefinition} Definition */
/** @typedef {'speed'|'clean'|'collector'} Medal */
/** @typedef {{score:number,elapsed:number,hits:number,deaths:number,retries:number,bits:number,chain:number,chainTime:number,maxChain:number,credited:string[],eligible:boolean,finished:boolean}} Run */
/** @typedef {{score:number,time:number,bonus:number,medals:Medal[],newBest:boolean,timeDelta:number|null,newMedals:Medal[],timeBonus:number}} Result */
/** @typedef {{bestScore:number,bestTime:number|null,medals:Medal[],maxChain:number}} Record */
/** @typedef {{run:Run,records:{[id:string]:Record},race?:import('./race.js').Race,best?:import('./race.js').Best}} Arcade */
/** @param {boolean} [eligible] @returns {Run} */
export function createRun(eligible = true) {
  return { score: 0, elapsed: 0, hits: 0, deaths: 0, retries: 0, bits: 0, chain: 0, chainTime: 0, maxChain: 0, credited: [], eligible, finished: false };
}
/** @param {Run} run @param {number} dt */
export function tickRun(run, dt) {
  if (run.finished || !Number.isFinite(dt) || dt <= 0) return;
  run.elapsed = Math.min(359999, run.elapsed + dt);
  run.chainTime = Math.max(0, run.chainTime - dt);
  if (!run.chainTime) run.chain = 0;
}
/** Credit authored events once across checkpoint retries. Stomp + defeat share a key.
 * @param {Run} run @param {import('./types.js').GameEvent} event @returns {number}
 */
export function credit(run, event) {
  if (run.finished) return 0;
  if (event.type === 'damaged' || event.type === 'failed') {
    if (event.type === 'damaged') run.hits++; else run.deaths++;
    run.chain = run.chainTime = 0; return 0;
  }
  let points = 0, key = '', enemy = false;
  if (event.entityId) {
    if (event.type === 'enemy-stomped' || event.type === 'enemy-defeated') { points = 200; key = `enemy:${event.entityId}`; enemy = true; }
    if (event.type === 'block-broken') { points = 50; key = `cache:${event.entityId}`; }
    if (event.type === 'reward-spawned' && event.reward === 'bit') { points = 100; key = `bit:${event.entityId}`; }
    if (event.type === 'pickup') { points = 500; key = `pickup:${event.entityId}`; }
    if (event.type === 'checkpoint') { points = 250; key = `checkpoint:${event.entityId}`; }
    if (event.type === 'boss-hit') { points = 250; key = `boss:${event.entityId}`; }
  }
  if (event.type === 'secret-returned' && event.entityId) { points = event.amount ?? 0; key = `secret:${event.entityId}`; }
  if (event.type === 'signal-captured') { points = event.amount ?? 500; key = 'signal:captured'; }
  if (event.type === 'boss-defeated') { points = 3000; key = 'boss:defeated'; }
  if (!points || run.credited.includes(key)) return 0;
  run.credited.push(key);
  if (enemy) {
    run.chain = Math.min(5, run.chain + 1); run.chainTime = 4;
    run.maxChain = Math.max(run.maxChain, run.chain); points *= run.chain;
  }
  if (event.type === 'reward-spawned') run.bits++;
  run.score = Math.min(99999999, run.score + points);
  return points;
}
/** Complete once before the automatic uplink advances to the next route.
 * @param {Run} run @param {Definition} level @param {{[id:string]:Record}} records @param {number} [attemptTime] @returns {Result}
 */
export function finishRun(run, level, records, attemptTime = run.elapsed) {
  const time = Math.round(run.elapsed * 1000) / 1000;
  const clean = run.eligible && run.hits === 0 && run.deaths === 0 && run.retries === 0;
  const speed = run.eligible && time <= targetTime(level);
  const collector = availableBits(level) > 0 && run.bits >= availableBits(level);
  const medals = MEDALS.filter(m => ({ speed, clean, collector })[m]);
  const timeBonus = run.eligible && !run.retries && !run.deaths ? Math.max(0, Math.floor(timeLimit(level) - attemptTime)) * 10 : 0;
  const bonus = timeBonus + 1000 + (clean ? 1000 : 0) + (collector ? 1000 : 0) + (speed ? Math.max(0, Math.floor(targetTime(level) - time)) * 25 : 0);
  const previous = records[level.id];
  if (!run.finished) run.score = Math.min(99999999, run.score + bonus);
  const newBest = !run.finished && (!previous || run.score > previous.bestScore);
  run.finished = true;
  records[level.id] = {
    bestScore: Math.max(previous?.bestScore ?? 0, run.score),
    bestTime: run.eligible ? Math.min(previous?.bestTime ?? Infinity, time) : previous?.bestTime ?? null,
    medals: MEDALS.filter(m => medals.includes(m) || previous?.medals.includes(m)),
    maxChain: Math.max(previous?.maxChain ?? 0, run.maxChain),
  };
  return { score: run.score, time, bonus, medals, newBest, timeBonus, timeDelta: typeof previous?.bestTime !== 'number' ? null : time - previous.bestTime, newMedals: medals.filter(m => !previous?.medals.includes(m)) };
}
/** Treat local storage as untrusted data; emit only known bounded fields.
 * @param {unknown} raw @param {readonly Definition[]} routes @param {Definition} current @param {boolean} [legacyLayout] @returns {Arcade|null}
 */
export function readArcade(raw, routes, current, legacyLayout = false) {
  if (!raw || typeof raw !== 'object') return null;
  const a = /** @type {Arcade} */ (raw), r = a.run;
  const integer = (/** @type {unknown} */ n, /** @type {number} */ max) => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= max;
  const duration = (/** @type {unknown} */ n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 359999;
  if (!r || !integer(r.score, 99999999) || !duration(r.elapsed) || !['hits', 'deaths', 'retries'].every(k => integer(r[/** @type {'hits'|'deaths'|'retries'} */(k)], 99999)) || !integer(r.bits, legacyLayout && current.bandwidthGoal ? 1000 : availableBits(current)) || !integer(r.chain, 5) || !integer(r.maxChain, 5) || !duration(r.chainTime) || r.chainTime > 4 || typeof r.eligible !== 'boolean' || typeof r.finished !== 'boolean') return null;
  const keys = new Set(['boss:defeated', 'signal:captured']);
  for (const s of current.solids) { keys.add(`cache:${s.id}`); keys.add(`bit:${s.id}`); keys.add(`pickup:${s.id}-reward`); }
  for (const p of current.portals ?? []) keys.add(`secret:${p.id}`);
  for (const e of current.enemies) keys.add(`enemy:${e.id}`);
  for (const c of current.checkpoints ?? []) keys.add(`checkpoint:${c.id}`);
  for (let h = 1; h < (current.boss?.health ?? 0); h++) keys.add(`boss:coupling-${h}`);
  const retired = (/** @type {string} */ key) => legacyLayout && current.bandwidthGoal && /^(?:(?:cache|bit|pickup|enemy|checkpoint):act2-\d+-(?:step|rise|upper|reward(?:-reward)?|monolith|checkpoint)|(?:cache|bit):quantum-block|pickup:quantum-block-reward|secret:quantum-(?:pipe|block-gate|leap))$/.test(key);
  if (!Array.isArray(r.credited) || r.credited.length > keys.size + (legacyLayout ? 128 : 0) || !r.credited.every(k => typeof k === 'string' && (keys.has(k) || retired(k))) || new Set(r.credited).size !== r.credited.length) return null;
  if (!a.records || typeof a.records !== 'object' || Array.isArray(a.records) || Object.keys(a.records).length > routes.length) return null;
  /** @type {{[id:string]:Record}} */ const records = {};
  for (const [id, record] of Object.entries(a.records)) {
    if (!routes.some(d => d.id === id) || !record || !integer(record.bestScore, 99999999) || record.bestTime !== null && !duration(record.bestTime) || !Array.isArray(record.medals) || record.medals.length > 3 || !record.medals.every(m => MEDALS.includes(m)) || record.maxChain !== undefined && !integer(record.maxChain, 5)) return null;
    records[id] = { bestScore: record.bestScore, bestTime: record.bestTime, medals: [...new Set(record.medals)], maxChain: record.maxChain ?? 0 };
  }
  const race = readRace(a.race, routes, current), best = readBest(a.best, routes);
  if (!race || !best) return null;
  return { race, best, run: { score: r.score, elapsed: r.elapsed, hits: r.hits, deaths: r.deaths, retries: r.retries, bits: Math.min(r.bits, availableBits(current)), chain: r.chain, chainTime: r.chainTime, maxChain: r.maxChain, credited: r.credited.filter(k => keys.has(k)), eligible: r.eligible && r.credited.every(k => keys.has(k)), finished: r.finished }, records };
}
